import { jsPDF } from "jspdf";
import { airlineLogo } from "@/mock";

const rgb = (h) => [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16));
const INR = (n) => "Rs. " + Number(n || 0).toLocaleString("en-IN");
const fmtD = (s, o) => new Date(`${s}T00:00:00`).toLocaleDateString("en-IN", o);

// item = { legs: [flight + {from,to,date}], legFares: [per-adult] | null, fare: per-adult total }
export const quoteTotals = (item, pax, mk = {}) => {
  const base = (item.fare || 0) * pax;
  const v = Number(mk.flight || 0);
  const add = v <= 0 ? 0 : Math.round(mk.flightType === "percent" ? base * v / 100 : v);
  const total = base + add;
  if (!item.legFares) return { total, legs: null };
  let left = add;
  const legs = item.legFares.map((p, i) => {
    const b = p * pax;
    const share = i === item.legFares.length - 1 ? left : Math.round(base ? add * b / base : 0);
    left -= share;
    return b + share;
  });
  return { total, legs };
};

const toPng = (url) => new Promise((resolve) => {
  const img = new Image();
  img.crossOrigin = "anonymous";
  const t = setTimeout(() => resolve(null), 4000);
  const done = (v) => { clearTimeout(t); resolve(v); };
  img.onload = () => {
    try {
      const c = document.createElement("canvas");
      c.width = 120; c.height = 120;
      const x = c.getContext("2d");
      x.fillStyle = "#fff"; x.fillRect(0, 0, 120, 120);
      x.drawImage(img, 10, 10, 100, 100);
      done(c.toDataURL("image/png"));
    } catch { done(null); }
  };
  img.onerror = () => done(null);
  img.src = url;
});

const drawLeg = (d, f, logo, x, y, navy, gold, label) => {
  if (logo) d.addImage(logo, "PNG", x, y + 4, 28, 28);
  else { d.setFillColor(...gold); d.circle(x + 14, y + 18, 14, "F"); d.setTextColor(...navy); d.setFont("helvetica", "bold"); d.setFontSize(9); d.text(f.code || "", x + 14, y + 21, { align: "center" }); }
  if (label) { d.setTextColor(...navy); d.setFont("helvetica", "bold"); d.setFontSize(6.5); d.text(label, x + 38, y + 4, { charSpace: 1 }); }
  d.setTextColor(30, 41, 59); d.setFont("helvetica", "bold"); d.setFontSize(9.5);
  d.text(f.airline || "", x + 38, y + 17, { maxWidth: 100 });
  d.setFont("helvetica", "normal"); d.setFontSize(7.5); d.setTextColor(100, 116, 139);
  d.text(`${f.flightNo || ""}  |  ${fmtD(f.date, { day: "2-digit", month: "short" })}`, x + 38, y + 29);

  const cx = x + 150;
  d.setTextColor(15, 23, 42); d.setFont("helvetica", "bold"); d.setFontSize(16);
  d.text(f.depTime || "", cx, y + 20);
  d.text(f.arrTime || "", cx + 180, y + 20, { align: "right" });
  d.setFont("helvetica", "normal"); d.setFontSize(8.5); d.setTextColor(100, 116, 139);
  d.text(f.from, cx, y + 33);
  d.text(f.to, cx + 180, y + 33, { align: "right" });
  d.setFontSize(7.5); d.text(f.duration || "", cx + 90, y + 8, { align: "center" });
  d.setDrawColor(203, 213, 225); d.setLineDashPattern([3, 3], 0); d.line(cx + 50, y + 15, cx + 130, y + 15); d.setLineDashPattern([], 0);
  d.setFillColor(...gold); d.circle(cx + 90, y + 15, 2.5, "F");
  d.setFont("helvetica", "bold");
  d.setTextColor(...(f.stops === 0 ? [5, 150, 105] : [217, 119, 6]));
  d.text(f.stops === 0 ? "Non-stop" : `${f.stops} Stop${f.via ? " via " + f.via : ""}`, cx + 90, y + 28, { align: "center" });
};

export async function buildQuotationPdf({ items, from, to, fromCity, toCity, date, retDate, pax, cabin, markup, brand, contact, clientName, qno: savedNo }) {
  const d = new jsPDF({ unit: "pt", format: "a4" });
  const W = d.internal.pageSize.getWidth();
  const H = d.internal.pageSize.getHeight();
  const navy = rgb(brand.primary || "#1B1F5B");
  const gold = rgb(brand.secondary || "#F5B32B");
  const light = gold.map((c) => Math.round(c + (255 - c) * 0.82));
  const logos = await Promise.all(items.map((it) => Promise.all(it.legs.map((f) => toPng(airlineLogo(f.code, 120))))));
  const round = items.some((it) => it.legs.length > 1);
  const qno = savedNo || `QT-${Date.now().toString().slice(-8)}`;

  const footer = () => {
    d.setFillColor(...navy); d.rect(0, H - 48, W, 48, "F");
    d.setTextColor(255, 255, 255); d.setFont("helvetica", "bold"); d.setFontSize(10);
    d.text(brand.name || "FLYTRIP", 40, H - 22);
    d.setFont("helvetica", "normal"); d.setFontSize(8.5);
    d.text([contact?.phonePretty, contact?.email].filter(Boolean).join("   |   "), W - 40, H - 22, { align: "right" });
  };

  d.setFillColor(...navy); d.rect(0, 0, W, 150, "F");
  d.setFillColor(...gold); d.rect(0, 150, W, 4, "F");
  d.setTextColor(255, 255, 255); d.setFont("helvetica", "bold"); d.setFontSize(24);
  d.text(brand.name || "FLYTRIP", 40, 52);
  d.setFont("helvetica", "normal"); d.setFontSize(9); d.setTextColor(210, 214, 235);
  d.text(round ? "ROUND TRIP FLIGHT QUOTATION" : "FLIGHT QUOTATION", 40, 70, { charSpace: 2 });
  d.text(qno, W - 40, 46, { align: "right" });
  d.text(`Issued ${new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}`, W - 40, 60, { align: "right" });
  d.setTextColor(255, 255, 255); d.setFont("helvetica", "bold"); d.setFontSize(30);
  d.text(from, 40, 118);
  const fw = d.getTextWidth(from);
  d.setTextColor(...gold); d.setFontSize(20); d.text(round ? "<->" : "->", 52 + fw, 115);
  d.setTextColor(255, 255, 255); d.setFontSize(30); d.text(to, (round ? 98 : 84) + fw, 118);
  d.setFont("helvetica", "normal"); d.setFontSize(9); d.setTextColor(210, 214, 235);
  d.text(`${fromCity}  to  ${toCity}${round ? "  and back" : ""}`, 40, 136);
  const o = { weekday: "short", day: "2-digit", month: "short", year: "numeric" };
  d.setFont("helvetica", "bold"); d.setFontSize(11); d.setTextColor(255, 255, 255);
  d.text(round && retDate ? `${fmtD(date, { day: "2-digit", month: "short" })} - ${fmtD(retDate, o)}` : fmtD(date, o), W - 40, 112, { align: "right" });
  d.setFont("helvetica", "normal"); d.setFontSize(9); d.setTextColor(210, 214, 235);
  d.text(`${pax} Traveller${pax > 1 ? "s" : ""}  |  ${cabin}`, W - 40, 128, { align: "right" });

  let y = 186;
  d.setTextColor(100, 116, 139); d.setFontSize(9);
  d.text(`${items.length} option${items.length > 1 ? "s" : ""} selected for you`, 40, y - 8);
  if (clientName) { d.setFont("helvetica", "bold"); d.setTextColor(...navy); d.text(`Prepared for: ${clientName}`, W - 40, y - 8, { align: "right" }); d.setFont("helvetica", "normal"); }

  items.forEach((it, i) => {
    const two = it.legs.length > 1;
    const h = two ? 132 : 78;
    if (y + h > H - 70) { footer(); d.addPage(); y = 40; }
    d.setDrawColor(226, 232, 240); d.setFillColor(255, 255, 255);
    d.roundedRect(40, y, W - 80, h, 10, 10, "FD");
    d.setFillColor(...navy); d.roundedRect(40, y, 6, h, 3, 3, "F");
    d.setTextColor(...navy); d.setFont("helvetica", "bold"); d.setFontSize(7);
    d.text(`OPTION ${i + 1}`, 58, y + 14, { charSpace: 1 });
    it.legs.forEach((f, li) => {
      if (li) { d.setDrawColor(226, 232, 240); d.line(58, y + 74, W - 190, y + 74); }
      drawLeg(d, f, logos[i][li], 58, y + 22 + li * 56, navy, gold, two ? (li ? "RETURN" : "ONWARD") : "");
    });

    const t = quoteTotals(it, pax, markup);
    const px = W - 40 - 130;
    d.setFillColor(...light); d.roundedRect(px, y + 12, 118, h - 24, 8, 8, "F");
    let py = y + 30;
    if (t.legs) {
      d.setFont("helvetica", "normal"); d.setFontSize(7.5); d.setTextColor(100, 116, 139);
      ["Onward", "Return"].forEach((lb, k) => {
        d.text(lb, px + 10, py); d.setTextColor(30, 41, 59); d.setFont("helvetica", "bold");
        d.text(INR(t.legs[k]), px + 108, py, { align: "right" });
        d.setFont("helvetica", "normal"); d.setTextColor(100, 116, 139); py += 14;
      });
      d.setDrawColor(...gold); d.line(px + 10, py - 6, px + 108, py - 6); py += 10;
    } else py += 4;
    d.setFontSize(7.5); d.setTextColor(100, 116, 139);
    d.text(`TOTAL FARE (${pax} PAX)`, px + 59, py, { align: "center" });
    d.setTextColor(...navy); d.setFont("helvetica", "bold"); d.setFontSize(15);
    d.text(INR(t.total), px + 59, py + 19, { align: "center" });
    d.setTextColor(100, 116, 139); d.setFont("helvetica", "normal"); d.setFontSize(7);
    d.text("incl. taxes & fees", px + 59, py + 31, { align: "center" });
    y += h + 14;
  });

  if (y + 30 > H - 60) { footer(); d.addPage(); y = 40; }
  d.setTextColor(100, 116, 139); d.setFontSize(8);
  ["Fares are subject to seat availability and may change until the ticket is issued.",
    "Valid for the selected dates only. Baggage and refund rules as per airline policy."]
    .forEach((l, k) => d.text(`- ${l}`, 40, y + 6 + k * 12));
  footer();

  return { doc: d, fileName: `Quotation_${from}-${to}_${date}${round && retDate ? "_" + retDate : ""}.pdf` };
}
